import {
  Box,
  EmptyState,
  FlexBox,
  ModalBody,
  ModalFoot,
  ModalHead,
  SelectionList,
  Text,
} from '@dixa/lib-shared-habla';
import _countBy from 'lodash/countBy';
import _find from 'lodash/find';
import _transform from 'lodash/transform';
import { SubmitHandler, useForm } from 'react-hook-form';
import { InjectedFormProps } from 'redux-form';
import { v4 as uuid } from 'uuid';

import { IntentDetection, intentDetectionApi } from 'apis/intentDetection';
import { HookFormSelectionList } from 'components/hookForm';
import { FlowNodeTypes } from 'components/powerFlowShared/components/types';
import { getConfirmButtonVerb } from 'components/powerFlowShared/helpers/getConfirmButtonVerb';
import type {
  ActionNodeT,
  FlowManagerEditModalComponentT,
} from 'fable/flowEditor';
import { useAppNavigate } from 'utils/url/useAppNavigate';

import type {
  FlowEmailNodeIntentDetectionT,
  IntentDetectionNodeRef,
} from '../nodeTypes';

type PropsT = FlowManagerEditModalComponentT<
  FlowEmailNodeIntentDetectionT | ActionNodeT
> &
  IntentDetectionModalPropsT;

type InjectedProps = InjectedFormProps<IntentDetectionModalPropsT, PropsT>;

export type IntentDetectionModalFormValuesT = {
  intentDetections: IntentDetection[];
};

export type IntentDetectionModalPropsT = {
  intentDetections: IntentDetectionNodeRef[];
};

type Props = PropsT & InjectedProps;

export function IntentDetectionModal(props: Props): JSX.Element {
  const { node, nodeLabel } = props;
  const { intentDetections, isFetching } =
    intentDetectionApi.useReadIntentDetectionsQuery(
      {},
      {
        selectFromResult: ({ data, ...rest }) => ({
          ...rest,
          intentDetections: [...(data?.intentDetections ?? [])].sort((a, b) => {
            return a?.name ? a.name.localeCompare(b?.name ?? '') : 0;
          }),
        }),
      },
    );

  const navigate = useAppNavigate();

  const handleRedirectToIntentDetectionSettings = (): void => {
    navigate({ path: '/settings/intent-detection' });
  };

  const intents = intentDetections.filter((intent) => {
    return props.node.payload?.intentDetections.find(
      (intentDetectionRef) => intentDetectionRef.id === intent.id,
    );
  });

  const form = useForm<IntentDetectionModalFormValuesT>({
    defaultValues: { intentDetections: intents },
    mode: 'all',
  });

  const watchIntentDetections = form.watch('intentDetections');

  const handleSaveClick: SubmitHandler<
    IntentDetectionModalFormValuesT
  > = async (values): Promise<void> => {
    const formIntentDetections = values.intentDetections;

    const intentDetectionRefs: IntentDetectionNodeRef[] =
      formIntentDetections.map((intent) => ({
        id: intent.id,
        nextNodeId: uuid(),
      }));

    const nodeOptions: FlowEmailNodeIntentDetectionT = {
      ...props.node,
      nodeType: FlowNodeTypes.INTENT_DETECTION,
      text: 'Intent detection',
      icon: 'lightbulb',
      isRemovable: true,
      isEditable: true,
      isAction: false,
      overlay: [],
      badges: [],
      payload: {
        intentDetections: intentDetectionRefs,
        defaultNextNodeId: uuid(),
      },
    };

    props.replaceNode(nodeOptions);

    props.triggerClose();

    return Promise.resolve();
  };

  function handleCancel(): void {
    props.triggerClose();
  }

  return (
    <Box style={{ width: '600px' }}>
      <form onSubmit={form.handleSubmit(handleSaveClick)}>
        <ModalHead title={nodeLabel} onClose={handleCancel}>
          Use AI to classify the purpose and intention of inbound messages.
        </ModalHead>
        <ModalBody>
          <div className="conversational-modal__columns">
            <HookFormSelectionList
              control={form.control}
              key={'id'}
              name={'intentDetections'}
              label={'Select intent detection'}
              componentProps={{
                clearable: true,
                placeholder: 'Select one or multiple intents',
                multiple: true,
                getItemTitleByValue: (
                  item: IntentDetection | null | undefined,
                ) => item?.name,
                equalityFn(a, b) {
                  return a?.id === b?.id;
                },
              }}
              rules={{
                required: 'Intent detection required',
                minLength: {
                  value: 1,
                  message: 'At least one intent detection required',
                },
                maxLength: {
                  value: 10,
                  message: 'At most 10 intent detections are allowed',
                },
              }}
            >
              {intentDetections.map((intent, index) => (
                <SelectionList.Item key={intent.id} value={intent}>
                  <FlexBox flexDirection="column">
                    <Text.Paragraph variant="3" color="primary">
                      {intent.name}
                    </Text.Paragraph>
                    <Text.Paragraph
                      variant="4"
                      color="secondary"
                      style={{ overflow: 'hidden', whiteSpace: 'nowrap' }}
                    >
                      {intent.description}
                    </Text.Paragraph>
                  </FlexBox>
                </SelectionList.Item>
              ))}
              {!isFetching && intentDetections.length === 0 && (
                <Box as="li" padding="250 300">
                  <EmptyState
                    title="No intent detections found"
                    description={
                      <>
                        {'Go to '}
                        <Text.ExternalLink
                          onClick={handleRedirectToIntentDetectionSettings}
                        >
                          intent detection settings
                        </Text.ExternalLink>{' '}
                        {'to get started.'}
                      </>
                    }
                  />
                </Box>
              )}
            </HookFormSelectionList>
          </div>
        </ModalBody>
        <ModalFoot
          confirmOnClick={form.handleSubmit(handleSaveClick)}
          confirmName={getConfirmButtonVerb(node)}
          confirmProps={{
            isLoading: isFetching,
            disabled: !(
              form.formState.isDirty && watchIntentDetections?.length > 0
            ),
          }}
          cancelOnClick={handleCancel}
        />
      </form>
    </Box>
  );
}
